import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import BottomNav from '../components/BottomNav';

export default function Editor() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Курсы
  const [courses, setCourses] = useState([]);
  const [editingCourseId, setEditingCourseId] = useState(null);
  const [courseForm, setCourseForm] = useState({ title: '', price: '', type: '' });
  
  // Состояния для изображений
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // О нас
  const [about, setAbout] = useState({
    yandexMapLink: '',
    addresses: [],
    schedule: []
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user || user.status !== 'staff') {
      navigate('/');
      return;
    }

    // Загрузка курсов
    fetch('/api/courses')
      .then(res => res.json())
      .then(setCourses)
      .catch(console.error);

    // Загрузка данных "О нас"
    fetch('/api/about')
      .then(res => res.json())
      .then(setAbout)
      .catch(console.error);
  }, [user, navigate]);

  // Обработчик выбора изображения
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // ────────────────────────────────────────────────
  // Курсы — добавление / редактирование / удаление
  // ────────────────────────────────────────────────
  const handleSubmitCourse = async (e) => {
    e.preventDefault();
    setLoading(true);

    const method = editingCourseId ? 'PUT' : 'POST';
    const url = editingCourseId ? `/api/courses/${editingCourseId}` : '/api/courses';

    try {
      const formData = new FormData();
      formData.append('title', courseForm.title);
      formData.append('price', courseForm.price);
      formData.append('type', courseForm.type);
      
      if (selectedImage) {
        formData.append('image', selectedImage);
      }

      const res = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      if (res.ok) {
        const updated = await res.json();
        if (editingCourseId) {
          setCourses(courses.map(c => c.id === editingCourseId ? updated : c));
        } else {
          setCourses([...courses, updated]);
        }
        setEditingCourseId(null);
        setCourseForm({ title: '', price: '', type: '' });
        setSelectedImage(null);
        setImagePreview(null);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditCourse = (course) => {
    setEditingCourseId(course.id);
    setCourseForm({ 
      title: course.title, 
      price: course.price, 
      type: course.type 
    });
    
    if (course.imageUrl) {
      setImagePreview(course.imageUrl);
    } else {
      setImagePreview(null);
    }
    setSelectedImage(null);
  };

  const handleDeleteCourse = async (id) => {
    try {
      const res = await fetch(`/api/courses/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        setCourses(courses.filter(c => c.id !== id));
      }
    } catch (err) {
      console.error(err);
    }
  };

  // ────────────────────────────────────────────────
  // О нас — редактирование
  // ────────────────────────────────────────────────
  const addAddress = () => {
    setAbout(prev => ({
      ...prev,
      addresses: [...prev.addresses, { id: Date.now(), name: '', address: '', phone: '' }]
    }));
  };

  const updateAddress = (id, field, value) => {
    setAbout(prev => ({
      ...prev,
      addresses: prev.addresses.map(a => a.id === id ? { ...a, [field]: value } : a)
    }));
  };

  const deleteAddress = (id) => {
    setAbout(prev => ({
      ...prev,
      addresses: prev.addresses.filter(a => a.id !== id)
    }));
  };

  const addSchedule = () => {
    setAbout(prev => ({
      ...prev,
      schedule: [...prev.schedule, { id: Date.now(), hours: '' }]
    }));
  };

  const updateSchedule = (id, value) => {
    setAbout(prev => ({
      ...prev,
      schedule: prev.schedule.map(s => s.id === id ? { ...s, hours: value } : s)
    }));
  };

  const deleteSchedule = (id) => {
    setAbout(prev => ({
      ...prev,
      schedule: prev.schedule.filter(s => s.id !== id)
    }));
  };

  const handleSaveAbout = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/about', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(about)
      });

      if (res.ok) {
        alert('Данные «О нас» сохранены!');
      } else {
        alert('Ошибка сохранения');
      }
    } catch (err) {
      console.error(err);
      alert('Ошибка сервера');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1b1b1b] text-[#dedede] font-['Inter',sans-serif] antialiased overflow-x-hidden">
      <Header />

      <section className="py-24 bg-[#1b1b1b]">
        <div className="max-w-screen-2xl mx-auto px-8">

          {/* Основной контейнер */}
          <div className="glass-panel rounded-[28px] p-8 md:p-12">
            {/* Редактирование курсов */}
            <h2 className="text-3xl font-black uppercase mb-8 text-center">
              Редактирование курсов
            </h2>

            <div className="space-y-6 mb-12">
              {courses.map(course => (
                <div key={course.id} className="glass-card p-6 rounded-xl flex justify-between items-center">
                  <div className="flex items-center gap-4">
                    {course.imageUrl && (
                      <img 
                        src={course.imageUrl} 
                        alt={course.title} 
                        className="w-16 h-16 object-cover rounded-lg"
                      />
                    )}
                    <div>
                      <h3 className="text-xl font-black uppercase">
                        {course.title}
                      </h3>
                      <p className="text-primary">{course.price}</p>
                      <p className="text-slate-400">Тип: {course.type}</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <button onClick={() => handleEditCourse(course)} className="text-gold hover:text-yellow-400">
                      Редактировать
                    </button>
                    <button onClick={() => handleDeleteCourse(course.id)} className="text-red-500 hover:text-red-400">
                      Удалить
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <form onSubmit={handleSubmitCourse} className="space-y-6 mb-16">
              <input
                className="w-full px-6 py-4 bg-white/5 border border-white/10 rounded-full backdrop-blur-md text-[#dedede] placeholder-slate-400 focus:border-primary focus:outline-none"
                placeholder="Название курса"
                value={courseForm.title}
                onChange={e => setCourseForm({ ...courseForm, title: e.target.value })}
                required
              />
              <input
                className="w-full px-6 py-4 bg-white/5 border border-white/10 rounded-full backdrop-blur-md text-[#dedede] placeholder-slate-400 focus:border-primary focus:outline-none"
                placeholder="Цена (например, 29 900 ₽)"
                value={courseForm.price}
                onChange={e => setCourseForm({ ...courseForm, price: e.target.value })}
                required
              />
              
              {/* Поле для загрузки фото */}
              <div className="space-y-3">
                <label className="block text-sm font-medium text-slate-300">
                  Изображение курса
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="w-full px-6 py-4 bg-white/5 border border-white/10 rounded-full backdrop-blur-md text-[#dedede] file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-white hover:file:bg-primary/80"
                />
                {imagePreview && (
                  <div className="mt-4">
                    <img 
                      src={imagePreview} 
                      alt="Превью" 
                      className="w-32 h-32 object-cover rounded-lg border-2 border-primary"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setImagePreview(null);
                        setSelectedImage(null);
                      }}
                      className="mt-2 text-sm text-red-500 hover:text-red-400"
                    >
                      Удалить
                    </button>
                  </div>
                )}
              </div>

              <select
                className="w-full px-6 py-4 bg-white/5 border border-white/10 rounded-full backdrop-blur-md text-[#dedede] focus:border-primary focus:outline-none"
                value={courseForm.type}
                onChange={e => setCourseForm({ ...courseForm, type: e.target.value })}
                required
              >
                <option value="">Тип транспорта</option>
                <option value="B">Легковые (B)</option>
                <option value="C">Грузовые (C)</option>
                <option value="A">Мотоциклы (A)</option>
                <option value="M">Мопеды и квадроциклы (M)</option>
              </select>
              
              <button
                type="submit"
                disabled={loading}
                className="w-full px-10 py-5 bg-primary text-white font-black rounded-full neon-glow-red uppercase tracking-widest text-sm hover:shadow-[0_0_50px_rgba(225,29,72,0.5)] transition-all disabled:opacity-50"
              >
                {loading ? 'Сохранение...' : editingCourseId ? 'Сохранить изменения' : 'Добавить курс'}
              </button>
            </form>

            {/* Редактирование «О нас» */}
            <div className="border-t border-white/10 pt-12 mt-12">
              <h2 className="text-3xl font-black uppercase mb-10 text-center">
                Редактирование страницы «О нас»
              </h2>

              <div className="space-y-10">
                {/* Яндекс.Карты */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-3">
                    Ссылка на Яндекс.Карты
                  </label>
                  <input
                    type="text"
                    value={about.yandexMapLink}
                    onChange={e => setAbout({ ...about, yandexMapLink: e.target.value })}
                    placeholder="https://yandex.ru/map-widget/v1/..."
                    className="w-full px-6 py-4 bg-white/5 border border-white/10 rounded-full backdrop-blur-md text-[#dedede] focus:border-primary focus:outline-none"
                  />
                </div>

                {/* Филиалы */}
                <div>
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-2xl font-black uppercase">Филиалы</h3>
                    <button
                      onClick={addAddress}
                      className="px-6 py-3 bg-secondary text-white rounded-full text-sm hover:bg-opacity-90 transition-all"
                    >
                      + Добавить филиал
                    </button>
                  </div>

                  {about.addresses.map(addr => (
                    <div key={addr.id} className="glass-card p-6 rounded-xl mb-6 space-y-4">
                      <input
                        value={addr.name}
                        onChange={e => updateAddress(addr.id, 'name', e.target.value)}
                        placeholder="Название филиала"
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-full text-[#dedede] focus:border-primary"
                      />
                      <input
                        value={addr.address}
                        onChange={e => updateAddress(addr.id, 'address', e.target.value)}
                        placeholder="Адрес"
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-full text-[#dedede] focus:border-primary"
                      />
                      <input
                        value={addr.phone}
                        onChange={e => updateAddress(addr.id, 'phone', e.target.value)}
                        placeholder="Телефон"
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-full text-[#dedede] focus:border-primary"
                      />
                      <button
                        onClick={() => deleteAddress(addr.id)}
                        className="text-red-500 hover:text-red-400 text-sm mt-2"
                      >
                        Удалить филиал
                      </button>
                    </div>
                  ))}
                </div>

                {/* График работы */}
                <div>
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-2xl font-black uppercase">График работы</h3>
                    <button
                      onClick={addSchedule}
                      className="px-6 py-3 bg-secondary text-white rounded-full text-sm hover:bg-opacity-90 transition-all"
                    >
                      + Добавить строку
                    </button>
                  </div>

                  {about.schedule.map(sch => (
                    <div key={sch.id} className="glass-card p-6 rounded-xl mb-6 flex items-center gap-4">
                      <input
                        value={sch.hours}
                        onChange={e => updateSchedule(sch.id, e.target.value)}
                        placeholder="Например: Пн-Вс: 9:00–21:00"
                        className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-full text-[#dedede] focus:border-primary"
                      />
                      <button
                        onClick={() => deleteSchedule(sch.id)}
                        className="text-red-500 hover:text-red-400 px-4 py-2"
                      >
                        Удалить
                      </button>
                    </div>
                  ))}
                </div>

                {/* Кнопка сохранения */}
                <button
                  onClick={handleSaveAbout}
                  disabled={loading}
                  className="w-full px-10 py-5 bg-primary text-white font-black rounded-full neon-glow-red uppercase tracking-widest text-sm hover:shadow-[0_0_50px_rgba(225,29,72,0.5)] transition-all disabled:opacity-50 mt-8"
                >
                  {loading ? 'Сохранение...' : 'Сохранить изменения «О нас»'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <BottomNav />
    </div>
  );
}